import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Droplet, Circle, ChevronRight } from "lucide-react";
import { listGoals } from "@/lib/api";

/**
 * TodayGoalStrip — a quiet, single-line goal cue at the top of the reader (Home).
 *
 * Design rules (strict, gracious):
 * - At MOST one quiet goal line shown.
 * - No counts, no progress fractions, no percentages, no pace/behind-schedule messaging.
 * - Priority: first active quantity goal. If none, render nothing.
 * - Tapping the line navigates to that goal's page (which contains the tracker).
 */
export default function TodayGoalStrip() {
    const [goal, setGoal] = useState(null);

    useEffect(() => {
        let cancelled = false;
        listGoals("active")
            .then(d => {
                if (cancelled) return;
                const goals = d.goals || [];
                const q = goals.find(g => g.goal_type === "quantity" && g.status === "active");
                setGoal(q || null);
            })
            .catch(() => { if (!cancelled) setGoal(null); });
        return () => { cancelled = true; };
    }, []);

    if (!goal) return null;

    const iconKind = (goal.quantity_config || {}).icon || "water";
    const Icon = iconKind === "water" ? Droplet : Circle;

    return (
        <Link
            to={`/goals/${goal.id}`}
            data-testid="today-goal-strip"
            className="mx-5 md:mx-8 mt-3 mb-1 px-3.5 py-2 rounded-full bg-white/70 border border-ink-10 hover:border-moss/40 hover:bg-white transition font-ui text-[12px] text-ink-70 inline-flex items-center gap-2 group max-w-max"
            title="Today's tracker"
        >
            <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                style={{ background: iconKind === "water" ? "rgba(156, 176, 192, 0.18)" : "rgba(183, 205, 177, 0.22)" }}
            >
                <Icon size={12} className={iconKind === "water" ? "text-[#6E8A9D]" : "text-moss-deep"} />
            </span>
            <span className="text-ink-40 uppercase tracking-[0.18em] text-[10px] font-semibold">Today</span>
            <span className="scripture text-[14px] text-ink truncate max-w-[24ch]">{goal.title}</span>
            <ChevronRight size={12} className="text-ink-40 group-hover:text-moss-deep transition" />
        </Link>
    );
}
